import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import NotificationBell from '../notifications/NotificationBell';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import { useManagerPayrollAccess } from '../../hooks/useManagerPayrollAccess';
import { IcoLock, IcoLogOut } from '../dashboard/DashIcons';
import '../../styles/admin-dashboard.css';

const verticalLogo = process.env.PUBLIC_URL + '/vertical-logo.svg';

function Header() {
  const { user, logout, isSuperAdmin } = useAuth();
  const { features } = useOrgSettings();
  const { hasAccess: hasMgrPayrollAccess } = useManagerPayrollAccess();
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const menuRef = useRef(null);

  const isAdminAssistant = user?.role === 'adminassistant';
  const isManager = user?.role === 'manager';
  const hasElevatedAccess = isSuperAdmin() || isAdminAssistant;

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const goToSettings = (section) => {
    setShowUserMenu(false);
    navigate(`/settings#${section}`);
  };

  const navTo = (path) => {
    setShowMobileMenu(false);
    navigate(path);
  };

  useEffect(() => {
    setShowMobileMenu(false);
  }, [location.pathname]);

  const roleBadge = () => {
    if (user?.role === 'superadmin')    return { label: 'Super Admin',    bg: '#fef3c7', color: '#92400e' };
    if (user?.role === 'admin')         return { label: 'Admin',          bg: '#fde8d8', color: '#9a3412' };
    if (isAdminAssistant)               return { label: 'Assistant Admin', bg: '#e0f2fe', color: '#0369a1' };
    if (user?.role === 'manager')       return { label: 'Manager',        bg: '#e0e7ff', color: '#4338ca' };
    if (user?.role === 'teamlead')      return { label: 'Team Lead',      bg: '#f3e8ff', color: '#7e22ce' };
    return { label: 'Employee', bg: '#f3f4f6', color: '#374151' };
  };

  const badge = roleBadge();

  return (
    <>
      <header className="dashboard-header">
        <div className="logo">
          <img src={verticalLogo} alt="Clock Earnings" className="header-logo-desktop" />
        </div>

        <nav className="main-nav">
          <button
            className={`nav-item ${location.pathname === '/' ? 'active' : ''}`}
            onClick={() => navigate('/')}
          >
            Dashboard
          </button>

          {/* Superadmin + Admin Assistant see Managers and Departments */}
          {hasElevatedAccess && (
            <>
              <button
                className={`nav-item ${isActive('/setup-managers') ? 'active' : ''}`}
                onClick={() => navigate('/setup-managers')}
              >
                Managers
              </button>
              <button
                className={`nav-item ${isActive('/setup-departments') ? 'active' : ''}`}
                onClick={() => navigate('/setup-departments')}
              >
                Departments
              </button>
            </>
          )}

          <button
            className={`nav-item ${isActive('/team') || isActive('/employee') ? 'active' : ''}`}
            onClick={() => navigate('/team')}
          >
            My Team
          </button>

          {features.scheduling && (
            <button
              className={`nav-item ${isActive('/schedule') ? 'active' : ''}`}
              onClick={() => navigate('/schedule')}
            >
              Schedule
            </button>
          )}

          {features.timeEntry && (isSuperAdmin() || isAdminAssistant) && (
            <button
              className={`nav-item ${isActive('/time-cards') ? 'active' : ''}`}
              onClick={() => navigate('/time-cards')}
            >
              Time Entry
            </button>
          )}

          {/* Payroll — superadmin, admin assistant, and manager */}
          {features.payroll && (isSuperAdmin() || isAdminAssistant || isManager) && (
            <button
              className={`nav-item ${isActive('/payroll') ? 'active' : ''}`}
              onClick={() => navigate('/payroll')}
            >
              Payroll
            </button>
          )}

          {/* Manager Payroll — private, only visible to owner + granted users */}
          {hasMgrPayrollAccess && (
            <button
              className={`nav-item ${isActive('/manager-payroll') ? 'active' : ''}`}
              onClick={() => navigate('/manager-payroll')}
              title="Private manager payroll"
            >
              <><IcoLock size={14} color="currentColor"/> Manager Pay</>
            </button>
          )}

          {/* Manager Time Entry — superadmin and admin only */}
          {features.payroll && (isSuperAdmin() || user?.role === 'admin') && (
            <button
              className={`nav-item ${isActive('/manager-time-entry') ? 'active' : ''}`}
              onClick={() => navigate('/manager-time-entry')}
            >
              Mgr Time Entry
            </button>
          )}

          {/* PTO — superadmin and manager */}
          {features.ptoTracking && (isSuperAdmin() || isManager) && (
            <button
              className={`nav-item ${isActive('/pto') ? 'active' : ''}`}
              onClick={() => navigate('/pto')}
            >
              PTO
            </button>
          )}

          <button
            className={`nav-item ${isActive('/tutorial') ? 'active' : ''}`}
            onClick={() => navigate('/tutorial')}
          >
            Help
          </button>
        </nav>

        {/* Hamburger — mobile only */}
        <button
          className={`hamburger-btn${showMobileMenu ? ' open' : ''}`}
          onClick={() => setShowMobileMenu(v => !v)}
          aria-label={showMobileMenu ? 'Close menu' : 'Open menu'}
        >
          <span /><span /><span />
        </button>

        {/* Notification bell — superadmin and manager */}
        {(isSuperAdmin() || isManager) && <NotificationBell />}

        {/* User menu dropdown */}
        <div style={{ position: 'relative' }} ref={menuRef}>
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            aria-haspopup="true"
            aria-expanded={showUserMenu}
            aria-label={`User menu for ${user?.firstName} ${user?.lastName}`}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              cursor: 'pointer', padding: '0.35rem 0.75rem',
              borderRadius: '8px', border: 'none',
              background: showUserMenu ? 'var(--ce-accent-subtle)' : 'transparent',
              transition: 'background 0.15s'
            }}
          >
            <div style={{
              width: '32px', height: '32px', borderRadius: '50%',
              background: 'linear-gradient(135deg, #0f4d2e 0%, #22c55e 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', fontSize: '0.75rem', fontWeight: '700', flexShrink: 0
            }}>
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </div>
            <span style={{ fontWeight: '600', fontSize: '0.9rem', color: 'var(--ce-user-name)' }}>{user?.firstName}</span>
            <span style={{
              fontSize: '0.7rem', background: badge.bg,
              color: badge.color, padding: '0.1rem 0.45rem', borderRadius: '99px', fontWeight: '600'
            }}>
              {badge.label !== 'Employee' ? badge.label : ''}
            </span>
            <span style={{ fontSize: '0.65rem', color: 'var(--ce-user-menu-subtext)' }}>{showUserMenu ? '▲' : '▼'}</span>
          </button>

          {showUserMenu && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 0.5rem)', right: 0,
              background: 'var(--ce-user-menu-bg)', borderRadius: '12px', width: '230px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.18)', border: '1px solid var(--ce-user-menu-border)',
              zIndex: 1000, overflow: 'hidden'
            }}>
              {/* User info */}
              <div style={{ padding: '1rem', borderBottom: '1px solid var(--ce-user-menu-border)', background: 'var(--ce-bg-surface)' }}>
                <p style={{ margin: 0, fontWeight: '700', fontSize: '0.9rem', color: 'var(--ce-user-menu-text)' }}>
                  {user?.firstName} {user?.lastName}
                </p>
                <p style={{ margin: '0.2rem 0 0', fontSize: '0.775rem', color: 'var(--ce-user-menu-subtext)' }}>{user?.email}</p>
                <span style={{
                  display: 'inline-block', marginTop: '0.4rem',
                  padding: '0.15rem 0.5rem', borderRadius: '99px', fontSize: '0.7rem', fontWeight: '700',
                  background: badge.bg, color: badge.color
                }}>
                  {badge.label}
                </span>
              </div>

              {/* Settings links */}
              <div style={{ padding: '0.5rem' }}>
                <p style={{ padding: '0.4rem 0.75rem', margin: 0, fontSize: '0.7rem', fontWeight: '700', color: 'var(--ce-user-menu-subtext)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Settings
                </p>
                {[
                  { label: 'Profile', section: 'profile' },
                  ...(isSuperAdmin() ? [
                    { label: 'Organization & Labels', section: 'organization' },
                    { label: 'System Preferences', section: 'system' },
                  ] : []),
                  { label: 'Notifications', section: 'notifications' },
                  ...(isSuperAdmin() ? [
                    { label: 'Subscription & Billing', section: 'subscription' },
                  ] : []),
                ].map(({ label, section }) => (
                  <button
                    key={section}
                    onClick={() => goToSettings(section)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: '0.55rem 0.75rem', background: 'none', border: 'none',
                      borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem',
                      color: 'var(--ce-user-menu-text)', fontWeight: '500'
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--ce-user-menu-hover)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Logout */}
              <div style={{ padding: '0.5rem', borderTop: '1px solid var(--ce-user-menu-border)' }}>
                <button
                  onClick={() => { setShowUserMenu(false); logout(); }}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '0.55rem 0.75rem', background: 'none', border: 'none',
                    borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem',
                    color: '#ef4444', fontWeight: '600'
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}
                >
                  <><IcoLogOut size={15}/> Log Out</>
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Mobile nav menu */}
      {showMobileMenu && (
        <>
          <div className="mobile-nav-overlay" onClick={() => setShowMobileMenu(false)} />
          <nav className="mobile-nav-menu">
            <button className={`mobile-nav-item${location.pathname === '/' ? ' active' : ''}`} onClick={() => navTo('/')}>
              Dashboard
            </button>
            {hasElevatedAccess && (
              <>
                <button className={`mobile-nav-item${isActive('/setup-managers') ? ' active' : ''}`} onClick={() => navTo('/setup-managers')}>
                  Managers
                </button>
                <button className={`mobile-nav-item${isActive('/setup-departments') ? ' active' : ''}`} onClick={() => navTo('/setup-departments')}>
                  Departments
                </button>
              </>
            )}
            <button className={`mobile-nav-item${isActive('/team') || isActive('/employee') ? ' active' : ''}`} onClick={() => navTo('/team')}>
              My Team
            </button>
            {features.scheduling && (
              <button className={`mobile-nav-item${isActive('/schedule') ? ' active' : ''}`} onClick={() => navTo('/schedule')}>
                Schedule
              </button>
            )}
            {features.timeEntry && (isSuperAdmin() || isAdminAssistant) && (
              <button className={`mobile-nav-item${isActive('/time-cards') ? ' active' : ''}`} onClick={() => navTo('/time-cards')}>
                Time Entry
              </button>
            )}
            {features.payroll && (isSuperAdmin() || isAdminAssistant || isManager) && (
              <button className={`mobile-nav-item${isActive('/payroll') ? ' active' : ''}`} onClick={() => navTo('/payroll')}>
                Payroll
              </button>
            )}
            {hasMgrPayrollAccess && (
              <button className={`mobile-nav-item${isActive('/manager-payroll') ? ' active' : ''}`} onClick={() => navTo('/manager-payroll')}>
                <><IcoLock size={14}/> Manager Pay</>
              </button>
            )}
            {features.payroll && (isSuperAdmin() || user?.role === 'admin') && (
              <button className={`mobile-nav-item${isActive('/manager-time-entry') ? ' active' : ''}`} onClick={() => navTo('/manager-time-entry')}>
                Mgr Time Entry
              </button>
            )}
            {features.ptoTracking && (isSuperAdmin() || isManager) && (
              <button className={`mobile-nav-item${isActive('/pto') ? ' active' : ''}`} onClick={() => navTo('/pto')}>
                PTO
              </button>
            )}
            <button className={`mobile-nav-item${isActive('/tutorial') ? ' active' : ''}`} onClick={() => navTo('/tutorial')}>
              Help
            </button>
          </nav>
        </>
      )}

      {!isHomePage && (
        <div className="mobile-back-bar">
          <button onClick={() => navigate('/')} className="mobile-back-btn">
            ← Dashboard
          </button>
        </div>
      )}
    </>
  );
}

export default Header;